import { useState } from 'react';
import { useDatos } from '../datos/DatosContext';
import { useAviso, useRetardo } from '../lib/avisos';
import { formatearFecha, isoHace, nuevoId } from '../lib/fechas';
import { Icono } from '../componentes/ui/Icono';
import { BloqueIA, Chip, Demo, GrupoChips, Pensando, TituloSeccion } from '../componentes/ui/basicos';
import type { Carta } from '../modelo/tipos';

type Pestana = 'natal' | 'rs' | 'ciclos' | 'consultas';
const PESTANAS: [Pestana, string][] = [['natal', 'Carta natal'], ['rs', 'Revoluciones solares'], ['ciclos', 'Tránsitos y ciclos'], ['consultas', 'Consultas']];
type PasoConsulta = 'inactivo' | 'confirmar' | 'cargando' | 'respuesta';

function FichaCarta({ carta }: { carta: Carta }) {
  return (
    <div className="stack">
      <section className="card stack">
        <div className="between">
          <div><span className="tag dusk">{carta.tipo}</span><h2 className="display" style={{ marginTop: 8 }}>{carta.titulo}</h2></div>
          <span className="tag moss"><Icono nombre="check" tam="sm" />{carta.verificacion}</span>
        </div>
        <dl className="meta" style={{ margin: 0 }}>
          <div><dt>Fecha y hora</dt><dd>{carta.fechaHora}</dd></div>
          <div><dt>Lugar del cálculo</dt><dd>{carta.lugar}</dd></div>
          <div><dt>Sistema de casas</dt><dd>{carta.sistemaCasas}</dd></div>
          <div><dt>Período</dt><dd>{carta.periodo}</dd></div>
          <div><dt>Archivo original</dt><dd>{carta.archivoOriginal}</dd></div>
        </dl>
      </section>
      <div className="grid2">
        <section className="card stack-sm">
          <h3>Posiciones verificadas</h3>
          <p className="small muted" style={{ margin: 0 }}>Datos de referencia. La IA no los modifica ni los calcula.</p>
          <div className="tblbox">
            <table className="tbl">
              <thead><tr><th scope="col">Punto</th><th scope="col">Signo</th><th scope="col">Grado</th></tr></thead>
              <tbody>{carta.posiciones.map((p) => <tr key={p.punto}><td>{p.punto}</td><td>{p.signo}</td><td>{p.grado}</td></tr>)}</tbody>
            </table>
          </div>
        </section>
        <div className="stack">
          <div className="ph"><Icono nombre="image" tam="lg" /><span>Imagen original de la carta</span><span>(aquí se verá tu archivo)</span></div>
          <section className="card stack-sm">
            <h3>Interpretaciones y notas</h3>
            <BloqueIA tipo="cita" etiqueta="Mi nota personal" pequeno>{carta.notaPersonal}</BloqueIA>
            <BloqueIA tipo="interp" etiqueta="Interpretación IA aprobada" pequeno>{carta.interpretacionIAAprobada}</BloqueIA>
          </section>
        </div>
      </div>
    </div>
  );
}

export function Astrologia() {
  const { datos, agregar } = useDatos();
  const aviso = useAviso();
  const retardo = useRetardo();
  const [pestana, setPestana] = useState<Pestana>('natal');
  const [rsElegida, setRsElegida] = useState('rs2627');
  const [comparar, setComparar] = useState(false);
  const [cartaConsulta, setCartaConsulta] = useState<string | null>(null);
  const [pregunta, setPregunta] = useState('');
  const [paso, setPaso] = useState<PasoConsulta>('inactivo');

  const natal = datos.cartas.find((c) => c.tipo === 'Carta natal');
  const revoluciones = datos.cartas.filter((c) => c.tipo === 'Revolución solar');
  const cartaVisible = pestana === 'natal' ? natal : revoluciones.find((c) => c.id === rsElegida);
  const [rsA, rsB] = revoluciones;
  const elegidaConsulta = datos.cartas.find((c) => c.id === cartaConsulta);

  const preparar = () => {
    if (!cartaConsulta) { aviso('Elige primero qué carta debo usar.'); return; }
    if (!pregunta.trim()) { aviso('Escribe tu pregunta.'); return; }
    setPaso('confirmar');
  };

  return (
    <div className="stack-lg">
      <header><p className="eyebrow">Mi mapa interior</p><h1 className="display">Astrología</h1></header>
      <div className="ai-block falta small">
        <strong>Datos de ejemplo ficticios.</strong> No corresponden a ninguna persona. Las posiciones reales solo se guardarán desde tus archivos originales o un cálculo verificado.
      </div>
      <div className="chips scroll" role="tablist" aria-label="Secciones de astrología">
        {PESTANAS.map(([id, etiqueta]) => (
          <button key={id} className={pestana === id ? 'chip on' : 'chip'} role="tab" aria-selected={pestana === id} onClick={() => setPestana(id)}>{etiqueta}</button>
        ))}
      </div>

      {pestana === 'rs' ? (
        <div className="between">
          <div className="chips" role="group" aria-label="Año de revolución solar">
            {revoluciones.map((r) => (
              <Chip key={r.id} activo={rsElegida === r.id && !comparar} onClick={() => { setRsElegida(r.id); setComparar(false); }}>
                {r.etiquetaCorta.replace('RS ', '')}
              </Chip>
            ))}
          </div>
          <Chip activo={comparar} onClick={() => setComparar((v) => !v)}>Comparar años</Chip>
        </div>
      ) : null}

      {(pestana === 'natal' || (pestana === 'rs' && !comparar)) && cartaVisible ? <FichaCarta carta={cartaVisible} /> : null}

      {pestana === 'rs' && comparar && rsA && rsB ? (
        <section className="card stack-sm">
          <TituloSeccion>Revoluciones solares comparadas</TituloSeccion>
          <p className="small muted" style={{ margin: 0 }}>Cada año se guarda por separado; comparar nunca sobrescribe. En rosa, lo que cambia.</p>
          <div className="tblbox">
            <table className="tbl">
              <thead><tr><th scope="col">Punto</th><th scope="col">{rsA.etiquetaCorta.replace('RS ', '')} · Sídney</th><th scope="col">{rsB.etiquetaCorta.replace('RS ', '')} · Melbourne</th></tr></thead>
              <tbody>
                {rsA.posiciones.map((p, i) => {
                  const a = p.signo + ' ' + p.grado;
                  const q = rsB.posiciones[i];
                  const b = q ? q.signo + ' ' + q.grado : '—';
                  return <tr key={p.punto} className={a === b ? '' : 'diff'}><td>{p.punto}</td><td>{a}</td><td>{b}</td></tr>;
                })}
              </tbody>
            </table>
          </div>
          <p className="small muted" style={{ margin: 0 }}>El Sol coincide en todas las revoluciones solares: es su definición.</p>
        </section>
      ) : null}

      {pestana === 'ciclos' ? (
        <div className="stack-sm">
          {datos.ciclos.map((c) => (
            <article key={c.id} className="card stack-sm">
              <div className="between"><h2 className="display" style={{ fontSize: 21 }}>{c.titulo}</h2><span className="tag">{c.tipo}</span></div>
              <div className="grid2">
                <BloqueIA tipo="cita" etiqueta="Datos y fechas" pequeno>{c.datos}</BloqueIA>
                <BloqueIA tipo="interp" etiqueta="Interpretación o fuente" pequeno>{c.interpretacion}</BloqueIA>
                <BloqueIA tipo="cita" etiqueta="Mi experiencia" pequeno>{c.experiencia}</BloqueIA>
                <BloqueIA tipo="cita" etiqueta="Revisar más adelante" pequeno>{c.revisar}</BloqueIA>
              </div>
            </article>
          ))}
        </div>
      ) : null}

      {pestana === 'consultas' ? (
        <div className="stack">
          <section className="card stack">
            <TituloSeccion>Hacer una consulta</TituloSeccion>
            <div className="stack-sm">
              <span className="lbl">¿Qué carta debo usar?</span>
              <GrupoChips etiqueta="Carta para la consulta" opciones={datos.cartas.map((c) => c.etiquetaCorta)}
                seleccion={elegidaConsulta?.etiquetaCorta ?? ''}
                onElegir={(et) => { setCartaConsulta(datos.cartas.find((c) => c.etiquetaCorta === et)?.id ?? null); setPaso('inactivo'); }} />
            </div>
            <div className="field">
              <label htmlFor="askq">Tu pregunta</label>
              <input id="askq" type="text" placeholder="Por ejemplo: ¿qué casa activa la Luna este año?" value={pregunta} onChange={(e) => setPregunta(e.target.value)} />
            </div>
            <div><button className="btn primary" onClick={preparar}>Preparar consulta</button></div>
          </section>

          {paso === 'confirmar' && elegidaConsulta ? (
            <div className="card dusk stack-sm">
              <span className="eyebrow" style={{ color: 'var(--dusk)' }}>Antes de responder</span>
              <p style={{ margin: 0 }}>Voy a usar <strong>{elegidaConsulta.titulo}</strong>, con los datos guardados para ese período, y ninguna otra carta. ¿Es correcto?</p>
              <div className="row">
                <button className="btn primary" onClick={() => { setPaso('cargando'); retardo(() => setPaso('respuesta'), 1300); }}>Sí, usar esa carta</button>
                <button className="btn" onClick={() => setPaso('inactivo')}>Cambiar</button>
              </div>
            </div>
          ) : null}
          {paso === 'cargando' ? <Pensando>Consultando solo esa carta…</Pensando> : null}
          {paso === 'respuesta' && elegidaConsulta ? (
            <div className="draft stack-sm">
              <div className="between"><span className="tag dusk">Borrador de IA · no guardado</span><Demo>Simulado</Demo></div>
              <BloqueIA tipo="cita" etiqueta={'Datos de la carta usada · ' + elegidaConsulta.titulo} pequeno>
                {elegidaConsulta.posiciones.map((p) => p.punto + ' en ' + p.signo + ' ' + p.grado).join(' · ') + '. Lugar: ' + elegidaConsulta.lugar + '. Casas: ' + elegidaConsulta.sistemaCasas + '.'}
              </BloqueIA>
              <BloqueIA tipo="interp" etiqueta="Interpretación (hipótesis)" pequeno>
                Respuesta de demostración: aquí la IA explicaría tu pregunta usando solo los datos de arriba, diferenciando lo que dice la carta de lo que es interpretación.
              </BloqueIA>
              <div className="row">
                <button className="btn primary" onClick={() => {
                  agregar('consultas', { id: nuevoId('q'), pregunta: pregunta.trim(), carta: elegidaConsulta.titulo, fecha: formatearFecha(isoHace(0)) });
                  setPaso('inactivo'); setPregunta('');
                  aviso('Consulta guardada con la carta que usó (solo en esta sesión).');
                }}>Guardar en mis consultas</button>
                <button className="btn quiet" onClick={() => setPaso('inactivo')}>Descartar</button>
              </div>
            </div>
          ) : null}

          <section className="stack-sm">
            <h2 className="eyebrow">Consultas guardadas</h2>
            {datos.consultas.map((c) => (
              <div key={c.id} className="card" style={{ padding: '14px 18px' }}>
                <p style={{ margin: 0, fontWeight: 600 }}>{c.pregunta}</p>
                <p className="small muted" style={{ margin: '4px 0 0' }}>{c.carta} · {c.fecha}</p>
              </div>
            ))}
          </section>
        </div>
      ) : null}
    </div>
  );
}
